import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../utils/connect';
import sportsApi from '../../utils/sportsApi';
import { shortDate } from '../../utils/formatters';
import { capitalize } from '../../utils/capitalize';

import { Row, Col, Flex, LoadingDots } from '@app/ui';
import './customContent.scss';
import { Context } from '../contexts/OnboardingContext';
import { SponsorContext } from '../contexts/SponsorContext';
import { PlacementContext } from '../contexts/PlacementContext';

const CustomContent = ({ client, type = 'placement' }) => {
    const { t } = useTranslation('settings');
    const { rhId } = useContext(Context);
    const [requestsLoading, setRequestsLoading] = useState(false);

    const { newRequests, setNewRequests, requested, setRequested } = useContext(
        type === 'placement' ? PlacementContext : SponsorContext,
    );

    const fetchRequested = async () => {
        if (!rhId) return;
        setRequestsLoading(true);
        try {
            const endPoint = `/clients/${client.id}/rightsholders/${rhId}/${type}s/requests`;

            const resp = await sportsApi.url(endPoint).get().json();

            if (Array.isArray(resp)) setRequested(resp);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
        } finally {
            setRequestsLoading(false);
        }
    };

    const resetRequested = () => {
        setRequested([]);
        setNewRequests([]);
    };

    useEffect(() => {
        fetchRequested();
        return () => {
            resetRequested();
        };
    }, [rhId]);

    const resetNewRequests = () => {
        setNewRequests([{ name: '' }]);
    };

    useEffect(() => {
        resetNewRequests();
    }, []);

    const addRow = () => {
        setNewRequests([...newRequests, { name: '' }]);
    };

    const removeRow = index => () => {
        const newReqs = newRequests.filter((r, i) => {
            return index != i;
        });
        setNewRequests(newReqs);
    };

    return (
        <div>
            <div className="gds-flex gds-flex--justify-between -m-v-2 -p-v-1">
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <p className="gds-text--regular gds-text--italic">
                        {t('clientConfiguration.customContent.requestCustom', {
                            type: capitalize(type),
                        })}
                    </p>
                </div>
                <div className="gds-flex__item gds-flex__item--grow-0">
                    <button
                        type="button"
                        className="gds-circular-button gds-circular-button--xs gds-circular-button--secondary"
                        onClick={addRow}
                    >
                        <i className="far fa-plus fa-motorcycle fa-lg" />
                    </button>
                </div>
            </div>
            {newRequests.map((req, index) => {
                const { name } = req;
                const changeName = e => {
                    const name = e.target.value;
                    let updatedRequests = [...newRequests];
                    updatedRequests[index] = { name };
                    setNewRequests(updatedRequests);
                };
                return (
                    <div className="gds-form-group" key={index}>
                        <div className="-m-b-3">
                            <div
                                className="gds-flex gds-flex--align-end"
                                style={{ width: '100%' }}
                            >
                                <div className="gds-flex__item">
                                    <div className="gds-flex-grid__row">
                                        <div className="gds-flex-grid__item gds-flex-grid__item--desktop-1 gds-flex-grid__item--tablet-1 gds-flex-grid__item--mobile-1">
                                            <div className="gds-form-group">
                                                <input
                                                    className="gds-form-group__text-input gds-form-group__text-input--sm"
                                                    type="text"
                                                    placeholder={t(
                                                        'clientConfiguration.customContent.namePlaceholder',
                                                        {
                                                            type: capitalize(
                                                                type,
                                                            ),
                                                        },
                                                    )}
                                                    required=""
                                                    minLength="1"
                                                    maxLength="50"
                                                    value={name}
                                                    onChange={changeName}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="gds-flex__item gds-flex__item--grow-0">
                                    <div
                                        className="gds-tooltip gds-tooltip--default gds-tooltip--left -m-l-1"
                                        data-tooltip={t(
                                            'clientConfiguration.customContent.remove',
                                        )}
                                    >
                                        <button
                                            className="gds-button gds-button--sm gds-button--link -p-a-2"
                                            type="button"
                                            onClick={removeRow(index)}
                                        >
                                            <i className="fas fa fa-minus-circle -color-tx-dan fa-lg" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}

            <div className="gds-divider">
                <span className="gds-divider__line" />
            </div>

            <div>
                <div className="gds-flex -m-b-2">
                    <p className="gds-text--regular gds-text--italic">
                        {t('clientConfiguration.customContent.pendingCustom', {
                            type: capitalize(type),
                        })}
                    </p>
                </div>
                {requestsLoading && (
                    <div
                        className="gds-flex gds-flex--justify-center gds-flex--align-center"
                        style={{ minHeight: '120px', width: '100%' }}
                    >
                        <LoadingDots size="lg" />
                    </div>
                )}
                {!requestsLoading && (
                    <Flex
                        vertical
                        className="gds-layout__container _layout_custom_requests_container"
                    >
                        <Row gutter={16}>
                            {requested
                                .sort((a, b) => {
                                    const _name = el =>
                                        type === 'placement'
                                            ? el.placementName
                                            : el.sponsorName;

                                    return _name(a).localeCompare(_name(b));
                                })
                                .map((p, i) => {
                                    const { status, createdAt, actionDate } = p;

                                    const name =
                                        type === 'placement'
                                            ? p.placementName
                                            : p.sponsorName;

                                    let statusComment = '';
                                    switch (status) {
                                        case 'Pending':
                                            statusComment = t(
                                                'clientConfiguration.customContent.requestedOn',
                                                {
                                                    date: shortDate(createdAt),
                                                },
                                            );
                                            break;
                                        case 'Approved':
                                            statusComment = t(
                                                'clientConfiguration.customContent.approvedOn',
                                                {
                                                    date: shortDate(actionDate),
                                                },
                                            );
                                            break;
                                        case 'Rejected':
                                            statusComment = t(
                                                'clientConfiguration.customContent.rejectedOn',
                                                {
                                                    date: shortDate(actionDate),
                                                },
                                            );
                                            break;
                                        default:
                                            return '';
                                    }

                                    const isPending = status === 'Pending';
                                    const isApproved = status === 'Approved';
                                    const isRejected = status === 'Rejected';
                                    return (
                                        <Col md={12} key={i}>
                                            <Flex
                                                vertical
                                                className="gds-layout__container _layout_custom_requests_container"
                                            >
                                                <Row gutter={16}>
                                                    <Col md={24}>
                                                        <div className="gds-card">
                                                            <div
                                                                className="gds-card__block -p-b-0 -p-h-3 -p-t-2 gds-tooltip--top"
                                                                style={{
                                                                    width: '100%',
                                                                }}
                                                                data-tooltip={`${name}`}
                                                            >
                                                                <p
                                                                    className="gds-text--bold gds-text--body-sm -ellipsis -color-tx-sec -p-t-1"
                                                                    style={{
                                                                        maxWidth:
                                                                            '100%',
                                                                    }}
                                                                >
                                                                    {isPending && (
                                                                        <i className="fa fa-fw fa-clock -m-r-1 -color-tx-war" />
                                                                    )}
                                                                    {isApproved && (
                                                                        <i className="fa fa-fw fa-check-circle -m-r-1 -color-tx-suc" />
                                                                    )}
                                                                    {isRejected && (
                                                                        <i className="fa fa-fw fa-times-circle -m-r-1 -color-tx-dan" />
                                                                    )}
                                                                    {name}
                                                                </p>
                                                                <p className="gds-text--body-sm gds-text--italic -ellipsis">
                                                                    {
                                                                        statusComment
                                                                    }
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Flex>
                                        </Col>
                                    );
                                })}
                        </Row>
                    </Flex>
                )}
            </div>
        </div>
    );
};

CustomContent.propTypes = {
    client: PropTypes.object,
    type: PropTypes.string,
};

export default connect(['client'])(CustomContent);
